import type { RegulatoryRiskCell, Row } from './RiskIndicators';
import { RISK_DOT_COLOR_MAP, RISK_STATE_CONFIG } from './RiskIndicatorConstants';
import { LEGAL_CASES_DIRECTION_BY_KEY, LEGAL_CASES_KEYS, hasLegalCasesCountsShape, resolveLegalCasesState } from './legalCasesRiskState';

// Identifier values that mean "no resolved DIN/CIN". A missing identifier is
// serialised as one of these literal strings (all truthy), so a plain
// `row.identifier ? …` check wrongly treats them as real.
const NULL_IDENTIFIER_SENTINELS = ['null', 'undefined'];

function hasUsableIdentifier(identifier?: string): boolean {
    const idf = (identifier ?? '').trim();
    return idf !== '' && !NULL_IDENTIFIER_SENTINELS.includes(idf.toLowerCase());
}

/**
 * Builds the mutually-exclusive lookup key for the regulatory-risk-hits query:
 * `{ identifier }` when the row carries a real identifier, else `{ entityName }`.
 * An entity screened without a resolved DIN/CIN has no identifier — the data
 * surfaces that as `"null"`/`"undefined"`/blank — so sending it as `identifier`
 * resolves to the wrong/absent entity and the drawer comes back empty. Kept in
 * sync with the backend's NULL_IDENTIFIER_SENTINELS normalization.
 */
export function resolveEntityQueryKey(row?: Pick<Row, 'identifier' | 'name'>): { identifier: string } | { entityName: string } {
    return hasUsableIdentifier(row?.identifier) ? { identifier: (row?.identifier ?? '').trim() } : { entityName: row?.name ?? '' };
}

/**
 * RECUR-27829. The design tints the whole Regulatory Checks cell, not just its text, and the
 * cell's 1rem padding lives on the table cell — so the fill has to be applied there rather
 * than on the inner span.
 *
 * Deliberately a pure module function rather than something returned from
 * useRiskIndicatorStates: it derives entirely from the row, needs none of the hook's state,
 * and hanging it off the hook meant every test that mocks the hook had to remember to stub
 * it. One that didn't threw `getRiskCellStyle is not a function` and took down the whole
 * table, including features unrelated to this change.
 *
 * Returns undefined (not an empty object) when there is no fill, so React leaves the cell's
 * existing styling untouched — used for both the pre-deploy fallback, where no state has
 * arrived yet, and for No Match, which the design draws with no fill at all.
 */
export function getRiskCellStyle(row?: Pick<Row, 'riskState'>, checkType?: string): { backgroundColor: string } | undefined {
    const cell = checkType ? row?.riskState?.[checkType] : undefined;
    if (!cell?.state) return undefined;

    // A legal-cases cell whose only open cases are disposed reads wire state CONFIRMED/PROBABLE
    // (red/amber tint) but the design shows No Open (amber) — route it through the resolver
    // instead of the wire state directly. Legacy-shape cells (old polus) fall through untouched.
    if (checkType && LEGAL_CASES_KEYS.has(checkType) && hasLegalCasesCountsShape(cell)) {
        const backgroundColor = resolveLegalCasesState(cell, LEGAL_CASES_DIRECTION_BY_KEY[checkType]).backgroundColor;
        return backgroundColor && backgroundColor !== 'transparent' ? { backgroundColor } : undefined;
    }

    const backgroundColor = RISK_STATE_CONFIG[cell.state as keyof typeof RISK_STATE_CONFIG]?.backgroundColor;
    return backgroundColor && backgroundColor !== 'transparent' ? { backgroundColor } : undefined;
}

const DOT_COLOR_BY_STATE: Record<string, string> = {
    CONFIRMED: RISK_DOT_COLOR_MAP.RED,
    PROBABLE: RISK_DOT_COLOR_MAP.YELLOW,
    NO_RISK: RISK_DOT_COLOR_MAP.GREEN,
    NOT_AVAILABLE: RISK_DOT_COLOR_MAP.NOT_AVAILABLE,
    NOT_CHECKED: RISK_DOT_COLOR_MAP.NOT_AVAILABLE,
    // No Open reads as "clear" (green tick), same as NO_RISK — added for legalCasesDotState below.
    NO_OPEN: RISK_DOT_COLOR_MAP.GREEN,
};

/**
 * RiskIndicatorCard's dot is a second renderer of the same signal getRiskCellStyle tints — a
 * legal-cases cell whose only open cases are disposed must not show the wire state's red/amber
 * dot either. Resolves to one of the LegalCasesStateId literals, which line up 1:1 with the
 * DOT_COLOR_BY_STATE keys above, so getRiskDotColor needs no changes of its own. Legacy-shape
 * cells and every non-legal checkType fall back to the wire state untouched.
 */
export function legalCasesDotState(checkType: string | undefined, cell: RegulatoryRiskCell | undefined): string | undefined {
    if (checkType && cell && LEGAL_CASES_KEYS.has(checkType) && hasLegalCasesCountsShape(cell)) {
        return resolveLegalCasesState(cell, LEGAL_CASES_DIRECTION_BY_KEY[checkType]).id;
    }
    return cell?.state;
}

/**
 * RECUR-27829. Colour for the risk dot (RiskIndicatorCard) — a SECOND, independent renderer
 * of the same signal as the table pill. Left on the score path it went red only at EXACTLY
 * composite 100, so the dot and the pill disagreed on the same row, and 12,498 entities sit
 * exactly on that boundary.
 *
 * Pure and exported so it can be asserted directly: the returned values are CSS custom
 * properties, and jsdom drops `var(...)` from an inline style, so the rendered dot's colour
 * cannot be read back in a test at all.
 */
export function getRiskDotColor(risk: unknown, state?: string): string {
    if (state && DOT_COLOR_BY_STATE[state]) return DOT_COLOR_BY_STATE[state];
    const n = Number(risk);
    if (risk === 0) return RISK_DOT_COLOR_MAP.GREEN;
    if (Number.isFinite(n) && n > 0 && n < 100) return RISK_DOT_COLOR_MAP.YELLOW;
    if (n === 100) return RISK_DOT_COLOR_MAP.RED;
    return RISK_DOT_COLOR_MAP.NOT_AVAILABLE;
}
